import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { TextEditorInput } from 'components/grid/hiGrid/style/StandardTanStackGrid.styles';
import { theme } from 'styles/theme';
import { formatCalcBasisCommas, formatCalcBasisCommasWithCaret } from 'utils/calcFormula';
import { useCodeSuggest } from 'hooks/useCodeSuggest';
import CodeSuggestPanel from 'components/common/CodeSuggestPanel';
import { showAlert } from 'utils/toastBridge';

const EditorShell = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100%;
  min-width: 0;
`;

const SearchAwareInput = styled(TextEditorInput)`
  padding-right: ${(p) => (p.$withSearch ? '26px' : undefined)};
`;

/** FormArea TextField 와 동일 — 입력 안쪽 코드조회 ··· */
const InnerSearchBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 2px;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  &:hover:not(:disabled) {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight || 'rgba(37, 99, 235, 0.08)'};
  }
`;

function buildPatchFromOption(suggest, option) {
  if (!option) return null;
  if (typeof suggest?.mapRow === 'function') {
    return suggest.mapRow(option.raw || option) || null;
  }
  const valueField = suggest?.valueField;
  const labelField = suggest?.labelField;
  if (!valueField && !labelField) return null;
  const patch = {};
  if (valueField) patch[valueField] = option.code;
  if (labelField) patch[labelField] = option.name;
  return patch;
}

/**
 * TanStack 그리드 셀 - 텍스트 인라인 에디터
 * showSearch: 입력 안쪽 ··· 코드조회 (FormArea TextField 와 동일 UX)
 * suggest: CodeSuggestConfig — LIKE 제안 + 확정 시 patch
 */
const TextCellEditor = ({
  value = '',
  initialKey,
  onSave,
  onCancel,
  onBoundaryArrow,
  onEnterMoveDown,
  onEnterMoveUp,
  onTabMove,
  onFillSelection,
  showSearch = false,
  onSearch,
  searchTitle = '코드 조회',
  formatCalcBasis = false,
  field,
  rowData,
  rowId,
  suggest = null,
}) => {
  const inputRef = useRef(null);
  const localValueRef = useRef('');
  const aliveRef = useRef(true);
  const graceUntilRef = useRef(0);
  const caretRef = useRef(null);
  const selectingRef = useRef(false);
  const isOpenedByKey = initialKey !== undefined;
  const [localValue, setLocalValue] = useState(() => {
    if (isOpenedByKey) {
      return formatCalcBasis ? formatCalcBasisCommas(initialKey ?? '') : (initialKey ?? '');
    }
    return formatCalcBasis ? formatCalcBasisCommas(value ?? '') : (value ?? '');
  });
  const [anchorRect, setAnchorRect] = useState(null);

  const suggestEnabled =
    !!suggest &&
    (typeof suggest.enabled === 'function' ? !!suggest.enabled(rowData) : suggest.enabled !== false);

  const getOptions = useCallback(
    (ctx) => {
      if (!suggest?.getOptions) return [];
      return suggest.getOptions({ ...ctx, row: rowData });
    },
    [suggest, rowData]
  );

  const {
    setQuery,
    options,
    loading,
    isOpen,
    close,
    highlightIndex,
    moveHighlight,
    resolveExact,
  } = useCodeSuggest({
    getOptions,
    minChars: suggest?.minChars ?? 1,
    maxResults: suggest?.maxResults ?? 20,
    debounceMs: suggest?.debounceMs ?? 200,
    enabled: suggestEnabled && !formatCalcBasis,
  });

  const updateAnchor = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchorRect({
      top: r.top,
      bottom: r.bottom,
      left: r.left,
      width: Math.max(r.width, 260),
    });
  }, []);

  useLayoutEffect(() => {
    graceUntilRef.current = Date.now() + 250;
    if (isOpenedByKey && inputRef.current) {
      const v = formatCalcBasis
        ? formatCalcBasisCommas(initialKey ?? '')
        : (initialKey ?? '');
      inputRef.current.value = v;
      setLocalValue(v);
      localValueRef.current = v;
      if (suggestEnabled) setQuery(v);
    }
  }, [initialKey, isOpenedByKey, formatCalcBasis, suggestEnabled, setQuery]);

  useEffect(() => {
    const nextRaw = isOpenedByKey ? (initialKey ?? '') : (value ?? '');
    const next = formatCalcBasis ? formatCalcBasisCommas(nextRaw) : nextRaw;
    setLocalValue(next);
    localValueRef.current = next;
    if (suggestEnabled && !isOpenedByKey) setQuery(String(next));
  }, [value, initialKey, isOpenedByKey, formatCalcBasis, suggestEnabled, setQuery]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateAnchor();
    const onScroll = () => updateAnchor();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, updateAnchor]);

  const setValueBoth = (next, caret) => {
    if (formatCalcBasis) {
      const { value: formatted, caret: nextCaret } = formatCalcBasisCommasWithCaret(
        next,
        caret ?? String(next).length
      );
      caretRef.current = nextCaret;
      localValueRef.current = formatted;
      setLocalValue(formatted);
      return;
    }
    localValueRef.current = next;
    setLocalValue(next);
    if (suggestEnabled) setQuery(next);
  };

  const commitSave = (v, opts) => {
    let str = (v ?? localValueRef.current ?? '').toString();
    if (formatCalcBasis) str = formatCalcBasisCommas(str);
    onSave?.(str, opts);
  };

  const applyOption = useCallback(
    (option, move) => {
      if (!option) return false;
      selectingRef.current = true;
      const patch = buildPatchFromOption(suggest, option);
      const display =
        patch && field != null && patch[field] != null
          ? String(patch[field])
          : suggest?.valueField === field
            ? option.code
            : suggest?.labelField === field
              ? option.name
              : option.code;
      localValueRef.current = display;
      setLocalValue(display);
      close();
      commitSave(display, { patch: patch || undefined, focusGrid: false });
      if (move === 'down') onEnterMoveDown?.();
      else if (move === 'up') onEnterMoveUp?.();
      else if (move === 'tab') onTabMove?.(false);
      else if (move === 'tabBack') onTabMove?.(true);
      window.setTimeout(() => {
        selectingRef.current = false;
      }, 0);
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commitSave는 onSave 래퍼
    [suggest, field, close, onEnterMoveDown, onEnterMoveUp, onTabMove, onSave]
  );

  const finishMove = (move) => {
    if (move === 'down') onEnterMoveDown?.();
    else if (move === 'up') onEnterMoveUp?.();
    else if (move === 'tab') onTabMove?.(false);
    else if (move === 'tabBack') onTabMove?.(true);
  };

  /** @returns {Promise<boolean>} 확정 성공 여부 */
  const tryCommitWithValidate = useCallback(
    async (rawVal, move) => {
      const val = (rawVal ?? localValueRef.current ?? '').toString();
      if (!suggestEnabled || suggest?.validateOnCommit === false) {
        commitSave(val, { focusGrid: false });
        finishMove(move);
        return true;
      }
      const trimmed = val.trim();
      if (!trimmed) {
        // 코드/명칭 비우면 쌍 필드 함께 클리어 (mapRow(null)로 UID 등 부가키도 초기화)
        let patch = {};
        if (typeof suggest?.mapRow === 'function') {
          const fromMap = suggest.mapRow(null);
          if (fromMap && typeof fromMap === 'object') patch = { ...fromMap };
        }
        if (suggest?.valueField) patch[suggest.valueField] = '';
        if (suggest?.labelField) patch[suggest.labelField] = '';
        const display = field && patch[field] != null ? patch[field] : '';
        commitSave(display, { patch, focusGrid: false });
        finishMove(move);
        return true;
      }
      // 목록 하이라이트 우선
      if (isOpen && options.length && options[highlightIndex]) {
        applyOption(options[highlightIndex], move);
        return true;
      }
      const resolved = await resolveExact(trimmed, rowData);
      if (resolved.ok && resolved.option) {
        applyOption(resolved.option, move);
        return true;
      }
      if (resolved.ok && !resolved.option) {
        commitSave(trimmed, { focusGrid: false });
        finishMove(move);
        return true;
      }
      showAlert(resolved.message || '등록된 코드/명칭이 없습니다.', 'warning');
      graceUntilRef.current = Date.now() + 400;
      inputRef.current?.focus();
      inputRef.current?.select();
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      suggestEnabled,
      suggest,
      field,
      isOpen,
      options,
      highlightIndex,
      applyOption,
      resolveExact,
      rowData,
      onEnterMoveDown,
      onEnterMoveUp,
      onTabMove,
      onSave,
    ]
  );

  const handleKeyDown = (e) => {
    if (suggestEnabled && isOpen && options.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        moveHighlight(1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        moveHighlight(-1);
        return;
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const val = e.target.value ?? localValueRef.current ?? '';
      if (e.ctrlKey || e.metaKey) {
        commitSave(val);
        onFillSelection?.(val);
        return;
      }
      if (suggestEnabled) {
        void tryCommitWithValidate(val, e.shiftKey ? 'up' : 'down');
        return;
      }
      commitSave(val, { focusGrid: false });
      if (e.shiftKey) onEnterMoveUp?.();
      else onEnterMoveDown?.();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (suggestEnabled) {
        void tryCommitWithValidate(e.target.value, e.shiftKey ? 'tabBack' : 'tab');
        return;
      }
      commitSave(e.target.value, { focusGrid: false });
      onTabMove?.(e.shiftKey ? 'left' : 'right');
      return;
    }
    if (e.key === 'Escape') {
      if (suggestEnabled && isOpen) {
        e.preventDefault();
        close();
        return;
      }
      onCancel?.();
      return;
    }
    if (e.key === 'F4' && suggestEnabled && typeof suggest?.onOpenLookup === 'function') {
      e.preventDefault();
      suggest.onOpenLookup({
        value: localValueRef.current ?? '',
        row: rowData,
        rowId,
        field,
        target: 'grid',
      });
      return;
    }
    const input = e.target;
    const v = input.value ?? '';
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    // Suggest 목록이 열려 있으면 좌우 화살표로 셀 이동하지 않음
    if (suggestEnabled && isOpen) return;
    if (e.key === 'ArrowLeft' && start === 0 && end === 0) {
      e.preventDefault();
      if (suggestEnabled) {
        void (async () => {
          const ok = await tryCommitWithValidate(v, null);
          if (ok) onBoundaryArrow?.('left');
        })();
      } else {
        commitSave(v, { focusGrid: false });
        onBoundaryArrow?.('left');
      }
      return;
    }
    if (e.key === 'ArrowRight' && start === v.length && end === v.length) {
      e.preventDefault();
      if (suggestEnabled) {
        void (async () => {
          const ok = await tryCommitWithValidate(v, null);
          if (ok) onBoundaryArrow?.('right');
        })();
      } else {
        commitSave(v, { focusGrid: false });
        onBoundaryArrow?.('right');
      }
    }
  };

  const handleFocus = (e) => {
    e.target.select();
    updateAnchor();
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (!aliveRef.current) return;
      if (selectingRef.current) return;
      const ae = document.activeElement;
      if (ae === inputRef.current) return;
      if (ae?.closest?.('[data-code-suggest-panel]')) return;
      if (Date.now() < graceUntilRef.current) {
        inputRef.current?.focus();
        return;
      }
      if (suggestEnabled) {
        void tryCommitWithValidate(localValueRef.current, null);
      } else {
        commitSave(localValueRef.current, { focusGrid: false });
      }
    }, 0);
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    graceUntilRef.current = Date.now() + 400;
    const val = localValueRef.current ?? '';
    onSearch?.({
      name: field,
      field,
      value: val,
      row: rowData,
      rowId,
    });
  };

  // 콤마 삽입 후 캐럿 복원
  useLayoutEffect(() => {
    if (!formatCalcBasis || caretRef.current == null || !inputRef.current) return;
    const pos = caretRef.current;
    caretRef.current = null;
    try {
      inputRef.current.setSelectionRange(pos, pos);
    } catch {
      /* ignore */
    }
  }, [localValue, formatCalcBasis]);

  const input = (
    <SearchAwareInput
      ref={inputRef}
      name="text"
      $withSearch={!!showSearch}
      value={localValue}
      onChange={(e) => setValueBoth(e.target.value, e.target.selectionStart)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoFocus
      autoComplete="off"
    />
  );

  const panel =
    suggestEnabled && isOpen ? (
      <CodeSuggestPanel
        open={isOpen}
        anchorRect={anchorRect}
        options={options}
        highlightIndex={highlightIndex}
        loading={loading}
        onSelect={(opt) => applyOption(opt, 'down')}
      />
    ) : null;

  if (!showSearch && !suggestEnabled) return input;

  return (
    <EditorShell data-cell-editor-shell>
      {input}
      {showSearch ? (
        <InnerSearchBtn
          type="button"
          title={searchTitle}
          aria-label={searchTitle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSearchClick}
        >
          {'\u00B7\u00B7\u00B7'}
        </InnerSearchBtn>
      ) : null}
      {panel}
    </EditorShell>
  );
};

export default TextCellEditor;
